import { useTranslation } from '@/lib/i18n';
import { formatKrwLedger } from '../lib/chargeMoney';
import type { OrdersSummary } from '../hooks/useOrdersSummaryQuery';

/**
 * QA-541 목록 머리 위 합계(텍스트) — 검색 결과 전체(쪽 무관)의 주문 수·수량·금액. 금액은 원화 합계.
 * BE 회신(order-service #101): 목록에 나오는 주문·상품 줄을 그대로 더한다 — 취소·미결제·결제실패도 포함이라 그렇게 표기한다.
 * 합계를 아직 못 받았으면(BE 반영 전·불러오는 중) 아무것도 그리지 않는다.
 */
export const OrderListTotals = ({ summary }: { summary: OrdersSummary | null | undefined }) => {
  const { t } = useTranslation('orders');
  if (!summary) return null;
  const n = (v: number) => v.toLocaleString('ko-KR');
  const parts: Array<[string, string, string]> = [
    ['orders', t('orders.totals.orderCount', '주문'), `${n(summary.orderCount)}${t('orders.totals.countUnit', '건')}`],
    ['quantity', t('orders.totals.itemQuantity', '수량'), `${n(summary.itemQuantity)}${t('orders.totals.quantityUnit', '개')}`],
    ['item-amount', t('orders.totals.itemAmount', '총상품금액'), formatKrwLedger(summary.itemAmount)],
    ['shipping', t('orders.totals.shippingFee', '배송비'), formatKrwLedger(summary.shippingFee)],
    ['payment', t('orders.totals.paymentAmount', '총 주문금액'), formatKrwLedger(summary.paymentAmount)],
  ];
  return (
    <p
      className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] tabular-nums"
      style={{ color: 'var(--content-muted)' }}
      aria-label={t('orders.totals.label', '목록 기준 합계(취소·미결제 포함)')}
      data-testid="order-list-totals"
    >
      <span className="font-medium" style={{ color: 'var(--content-default)' }}>
        {t('orders.totals.label', '목록 기준 합계(취소·미결제 포함)')}
      </span>
      {parts.map(([key, label, value]) => (
        <span key={key} data-testid={`order-list-totals-${key}`}>
          {label} <b style={{ color: 'var(--content-strong)' }}>{value}</b>
        </span>
      ))}
    </p>
  );
};
